<template>
  <section class="video-container">
    <div class="video-wrapper" :style="{ aspectRatio: mediaAspectRatio }">
      <!-- 视频 -->
      <video
        v-if="currentVideo"
        ref="videoPlayer"
        class="hero-video"
        :poster="currentPoster"
        muted
        autoplay
        playsinline
        webkit-playsinline
        preload="auto"
        @play="handlePlay"
        @pause="handlePause"
        @ended="handleEnded"
      >
        <source :src="currentVideo" type="video/mp4" />
      </video>

      <!-- 图片 -->
      <img v-else class="hero-video" :src="currentPoster" alt="top image" />

      <!-- overlay -->
      <div class="video-overlay" :style="{ background: overlayGradient }">
        <div class="model-info">
          <p v-if="videoData.tip" class="info-level">{{ videoData.tip }}</p>
          <h1 v-if="videoData.title" class="info-title">
            {{ videoData.title }}
          </h1>
          <p v-if="videoData.subtitle" class="info-desc">
            {{ videoData.subtitle }}
          </p>
        </div>
      </div>

      <!-- 播放/暂停按钮 -->
      <button
        v-if="currentVideo"
        class="video-control"
        @click.stop="togglePlay"
        :class="{
          playing: isPlaying,
          ended: isEnded,
        }"
        aria-label="Play / Pause"
      >
        <span class="icon"></span>
      </button>
    </div>
  </section>
</template>

<script>
export default {
  name: "Video",
  props: {
    videoData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isPlaying: false,
      isEnded: false,
      windowWidth: window.innerWidth,
      isMobile: window.innerWidth <= 768,
      overlayDarkness: 0.4,
      resizeTimer: null,
    };
  },
  computed: {
    mediaAspectRatio() {
      return this.isMobile ? "3/4" : "5/3";
    },
    currentVideo() {
      if (!this.videoData.video) return null;
      return this.isMobile && this.videoData.videoMobile
        ? `/assets/products/${this.videoData.videoMobile}`
        : `/assets/products/${this.videoData.video}`;
    },
    currentPoster() {
      return this.isMobile && this.videoData.topImgMobile
        ? `/assets/products/${this.videoData.topImgMobile}`
        : `/assets/products/${this.videoData.topImg || ""}`;
    },
    overlayGradient() {
      const dark = this.overlayDarkness;
      return `linear-gradient(to top, rgba(18,28,38,${dark}) 0%, rgba(30,40,50,0) 70%)`;
    },
  },
  methods: {
    togglePlay() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      if (video.paused) video.play();
      else video.pause();
      this.isPlaying = !video.paused;
    },
    handlePlay() {
      this.isPlaying = true;
      this.isEnded = false;
      this.checkVideoBrightness();
      // ✅ 视频播放时触发 AOS
      if (window.AOS) window.AOS.refresh();
    },
    handlePause() {
      this.isPlaying = false;
    },
    handleEnded() {
      this.isPlaying = false;
      this.isEnded = true;
    },
    checkVideoBrightness() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      // ✅ 防止宽高为0报错
      if (video.videoWidth === 0 || video.videoHeight === 0) return;

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth / 10;
      canvas.height = video.videoHeight / 10;
      const ctx = canvas.getContext("2d");
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let colorSum = 0;
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          colorSum += avg;
        }
        const brightness = colorSum / (canvas.width * canvas.height);
        this.overlayDarkness = brightness < 100 ? 0.6 : 0.3;
      } catch (err) {
        console.warn("checkVideoBrightness skipped: ", err);
      }
    },

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const prevMobile = this.isMobile;
        this.windowWidth = window.innerWidth;
        this.isMobile = this.windowWidth <= 768;
        if (prevMobile !== this.isMobile) {
          const video = this.$refs.videoPlayer;
          if (video) {
            video.load();
            video.onloadedmetadata = () => {
              this.checkVideoBrightness();
              video.play().catch(() => {});
            };
          }
        }
      }, 200);
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);

    const video = this.$refs.videoPlayer;
    if (video) {
      video.onloadedmetadata = () => {
        this.checkVideoBrightness();
      };
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    const video = this.$refs.videoPlayer;
    if (video) video.pause();
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.video-container {
  position: relative;
  overflow: hidden;

  .video-wrapper {
    position: relative;
    width: 100%;
    height: 100%;

    .hero-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .video-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: flex-end;
      padding: 2rem;

      .model-info {
        max-width: 880px;

        .info-level {
          font-size: 1.2rem;
          margin-bottom: 0.4rem;
          color: #fff;
        }
        .info-title {
          font-size: 2.4rem;
          color: #fff;
          margin-bottom: 0.3rem;
        }
        .info-desc {
          font-size: 1.3rem;
          color: #fff;
        }
      }
    }

    .video-control {
      position: absolute;
      bottom: 1.6rem;
      right: 1.6rem;
      z-index: 10;
      width: 2.8rem;
      height: 2.8rem;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(255, 255, 255, 0.25);
      border-radius: 50%;
      cursor: pointer;
      border: none;
      transition: 0.2s;

      &:hover {
        transform: scale(1.1);
        background: rgba(255, 255, 255, 0.45);
      }

      .icon {
        display: block;
        width: 0;
        height: 0;
      }

      /* ▶ Play */
      &:not(.playing) .icon {
        border-left: 14px solid #fff;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        margin-left: 3px;
      }

      /* ❚❚ Pause */
      &.playing .icon {
        width: 14px;
        height: 16px;
        border-left: 4px solid #fff;
        border-right: 4px solid #fff;
        border-top: none;
        border-bottom: none;
      }
    }
  }
}

@media (max-width: 768px) {
  .video-wrapper {
    height: auto;
    .hero-video {
      object-fit: contain;
    }
  }
}
</style>
